import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, resource } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { ActivityStream } from '@lib/activity-stream';
import { ExtraHoursTable } from '@lib/extra-hours-table';
import { ProductDetailTable } from '@lib/product-detail-table';
import { ProductForm, ProductFormData } from '@lib/product-form';

import { Auth, Jira, Lambda, ProductService } from '../service';
import { ProjectTable } from './project-table';
import { TimeReportingWidget } from './time-reporting-widget';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'product-detail-page.html',
  imports: [
    TimeReportingWidget,
    ProjectTable,
    ActivityStream,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTableModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    ProductDetailTable,
    ExtraHoursTable,
    RouterModule,
  ],
})
export class ProductDetailPage implements OnInit {
  route = inject(ActivatedRoute);
  location = inject(Location);
  jira = inject(Jira);
  auth = inject(Auth);

  router = inject(Router);

  kbLink = inject(Lambda).getKnowledgeBaseLink();

  productService = inject(ProductService);

  dialog = inject(MatDialog);
  pending = this.productService.pending;

  jobNumber = input.required<string>();

  product = computed(() => this.productService.products().find((it) => it.JobNumber === this.jobNumber()));

  probablyHoldedResource = resource({
    params: () => ({ product: this.product() }),
    defaultValue: false,
    loader: async ({ params }) => {
      if (!params.product) {
        return false;
      }

      const logs = await this.jira.getProjectTimelogs(params.product);
      const spentRecently = Math.max(...logs.time.slice(-3).map((it) => it[1].spent));
      return spentRecently <= 0;
    },
  });

  probablyHolded = this.probablyHoldedResource.value;

  constructor() {
    effect(() => {
      if (!this.product() && !this.pending()) {
        this.router.navigate(['/404']);
      }
    });
  }
  edit() {
    const product = this.product();

    if (!product) {
      console.warn('Attempt to open edit form without a product');
      return;
    }

    const data: ProductFormData = {
      title: 'Update product',
      product,
    };

    this.dialog
      .open(ProductForm, {
        data,
      })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reload(),
      });
  }

  ngOnInit() {
    if (this.route.snapshot.params['edit-form']) {
      this.edit();
    }
  }

  reload(): void {
    this.productService.reload();
  }
}
